/**
 * The screen-space reflection of each render of one scene. A sentinel that three draws first in the
 * opaque list traces it, as Godot traces between its depth prepass and its opaque pass
 * (`render_forward_clustered.cpp:2150`). One drawn last hides it from the alpha pass, which Godot
 * sets up with no screen-space effects (`:2381`, `:754-769`). The end of the render copies the
 * finished frame for the next trace (`:2413-2417`). Each world buffer keeps its own buffers and the
 * camera of its last frame.
 */
import * as THREE from 'three';
import { probeCaptureOf } from '../reflectionProbe/captureCameras.js';
import { drawOffscreen } from '../offscreen.js';
import { beginStep, endStep } from '../frameProfile/frameProfile.js';
import { createFirstOpaqueSentinel, createLastOpaqueSentinel, } from '../opaqueSentinel.js';
import { leaveSlot, showInSlot } from '../sharedTexture.js';
import { drawsTransparentBackground } from '../environment/toneMapPass.js';
import { ReflectionBuffers } from './reflectionBuffers.js';
import { copyLastFrame, traceReflection } from './reflectionPasses.js';
import { drawSamplePrepasses } from './normalRoughnessPrepass.js';
import { showBestFitNormals } from './bestFitNormals.js';
import { ReflectionFrames } from './reflectionFrames.js';
import { reflectionSlot, SSR_STATE, ssrState } from './ssrUniforms.js';
/**
 * Godot's `set_depth_correction(true)`: y flipped, so screen rows run top-down, and depth reversed
 * into 0 to 1 (`projection.cpp`, `ss_effects.cpp:1489`).
 */
const DEPTH_CORRECTION = new THREE.Matrix4().set(1, 0, 0, 0, 0, -1, 0, 0, 0, 0, -0.5, 0.5, 0, 0, 0, 1);
/** One world buffer's reflection, which lives as long as the buffer. */
class WorldReflection {
    buffers = null;
    /** Godot starts both at identity, over a last frame cleared to zero (`ss_effects.cpp:392`). */
    lastProjection = new THREE.Matrix4();
    lastView = new THREE.Matrix4();
    frames = new ReflectionFrames();
    buffersFor(renderer, shape) {
        if (this.buffers?.fits(shape))
            return this.buffers;
        this.release(renderer);
        this.buffers = new ReflectionBuffers(renderer, shape);
        return this.buffers;
    }
    /** Frees the buffers, first taking the slot off them: binding a deleted texture is an error. */
    release(renderer) {
        if (!this.buffers)
            return;
        leaveSlot(renderer, reflectionSlot, this.buffers.reflection.texture);
        this.buffers.dispose();
        this.buffers = null;
    }
}
/** Scratch for one trace. */
const projection = new THREE.Matrix4();
const inverseProjection = new THREE.Matrix4();
const reprojection = new THREE.Matrix4();
export class SceneReflections {
    renderer;
    settings;
    askFrame;
    firstSentinel;
    lastSentinel;
    worlds = new Map();
    /** Written by `begin` and `end`, as renders of the scene nest. */
    renders = [];
    constructor(renderer, settings, askFrame) {
        this.renderer = renderer;
        this.settings = settings;
        this.askFrame = askFrame;
        this.firstSentinel = createFirstOpaqueSentinel('ScreenSpaceReflection', (_renderer, scene, camera) => this.beginOpaque(scene, camera));
        this.lastSentinel = createLastOpaqueSentinel('ScreenSpaceReflectionEnd', () => this.endOpaque());
    }
    /** Starts a render of the scene through `camera` into `target`, null for the canvas. */
    begin = (camera, target) => {
        this.renders.push({
            camera,
            target,
            world: this.worldOf(camera, target),
            isTraced: false,
            isPastOpaque: false,
        });
        ssrState[SSR_STATE.showsReflection] = 0;
    };
    /**
     * Ends the innermost render: keeps its frame for the next trace, and shows the outer render's
     * reflection again while the outer render draws its opaque list.
     */
    end = () => {
        const render = this.renders.pop();
        if (render?.isTraced)
            this.finish(render);
        const outer = this.renders.at(-1);
        if (outer?.isTraced && !outer.isPastOpaque)
            this.show(outer.world.buffers);
        else
            ssrState[SSR_STATE.showsReflection] = 0;
    };
    dispose() {
        for (const [target, world] of this.worlds)
            this.release(target, world);
        this.firstSentinel.dispose();
        this.lastSentinel.dispose();
    }
    /**
     * A world buffer's reflection, or null where Godot traces none: a reflection probe's capture and a
     * viewport with a transparent background (`render_forward_clustered.cpp:1810-1852`). Every 3D
     * render draws into a world buffer (`toneMapPass.ts`), so the canvas is no world.
     */
    worldOf(camera, target) {
        if (!target || probeCaptureOf(camera) || drawsTransparentBackground(target))
            return null;
        let world = this.worlds.get(target);
        if (!world) {
            world = new WorldReflection();
            this.worlds.set(target, world);
            target.addEventListener('dispose', this.onTargetDispose);
        }
        return world;
    }
    onTargetDispose = (event) => {
        const world = this.worlds.get(event.target);
        if (world)
            this.release(event.target, world);
    };
    release(target, world) {
        target.removeEventListener('dispose', this.onTargetDispose);
        world.release(this.renderer);
        this.worlds.delete(target);
    }
    /**
     * Shows the innermost render's reflection from the start of each of its opaque lists, traced at the
     * first. three's transmission pass draws one before the colour pass, and the frame it refracts
     * shows the reflection, as the screen copy after Godot's opaque pass does (`:2337`).
     */
    beginOpaque(scene, camera) {
        const render = this.renders.at(-1);
        if (!render?.world)
            return;
        const buffers = render.isTraced ? render.world.buffers : this.trace(render, render.world, scene, camera);
        render.isPastOpaque = false;
        this.show(buffers);
    }
    /** Traces `render` into its world's buffers, and returns them. */
    trace(render, world, scene, camera) {
        const { ssr, isHalfSize } = this.settings();
        const { width, height, samples } = render.target;
        const buffers = world.buffersFor(this.renderer, { width, height, samples, isHalfSize });
        projection.multiplyMatrices(DEPTH_CORRECTION, camera.projectionMatrix);
        inverseProjection.copy(projection).invert();
        const inputs = {
            projection,
            inverseProjection,
            reprojection: this.reprojectionOf(world, camera),
            isOrthogonal: camera.isOrthographicCamera === true,
            ssr,
        };
        beginStep(this.renderer, 'Screen-space reflection');
        drawOffscreen(this.renderer, () => {
            showBestFitNormals(this.renderer);
            drawSamplePrepasses(this.renderer, scene, camera, buffers.samplePrepasses);
            traceReflection(this.renderer, buffers, inputs);
        });
        endStep(this.renderer, 'Screen-space reflection');
        render.isTraced = true;
        return buffers;
    }
    /** Ends an opaque list of the innermost render: the transmissive and transparent lists show none. */
    endOpaque() {
        const render = this.renders.at(-1);
        if (!render)
            return;
        render.isPastOpaque = true;
        ssrState[SSR_STATE.showsReflection] = 0;
    }
    /**
     * `last_projection * last_view * camera_world * projection^-1` (`render_forward_clustered.cpp:1469-1476`),
     * after which this frame's camera becomes the last frame's.
     */
    reprojectionOf(world, camera) {
        reprojection
            .multiplyMatrices(world.lastProjection, world.lastView)
            .multiply(camera.matrixWorld)
            .multiply(inverseProjection);
        world.lastProjection.copy(projection);
        world.lastView.copy(camera.matrixWorldInverse);
        return reprojection;
    }
    show(buffers) {
        showInSlot(this.renderer, reflectionSlot, buffers.reflection.texture);
        ssrState[SSR_STATE.showsReflection] = 1;
    }
    /** Copies the finished frame, and asks for the frame that first reads it. */
    finish(render) {
        const buffers = render.world.buffers;
        drawOffscreen(this.renderer, () => copyLastFrame(this.renderer, buffers, render.target.texture));
        if (render.world.frames.traced())
            this.askFrame();
    }
}
//# sourceMappingURL=sceneReflections.js.map